// frontend/src/pages/ProductClassificationsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import useApi from '../hooks/useApi';
import { 
    Plus, 
    Search, 
    Edit, 
    Trash2, 
    X, 
    Save, 
    Loader2, 
    List,
    AlertCircle
} from 'lucide-react';

const ProductClassificationsPage = ({ currentUser, onDataChange }) => {
    const { apiFetch, permissions } = useApi();
    const [classifications, setClassifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ name: '' });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const canCreate = permissions?.product_classifications_create || currentUser?.is_super_user;
    const canEdit = permissions?.product_classifications_edit || currentUser?.is_super_user;
    const canDelete = permissions?.product_classifications_delete || currentUser?.is_super_user;

    const fetchClassifications = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiFetch('/product-classifications');
            setClassifications(data || []);
        } catch (err) {
            console.error("Erro ao buscar classificações:", err);
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    useEffect(() => {
        fetchClassifications();
    }, [fetchClassifications]);

    const handleOpenModal = (item = null) => {
        setEditingItem(item);
        setFormData({ name: item ? item.name : '' });
        setError('');
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
        setFormData({ name: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');

        try {
            if (editingItem) {
                await apiFetch(`/product-classifications/${editingItem.id}`, {
                    method: 'PUT',
                    body: formData
                });
            } else {
                await apiFetch('/product-classifications', {
                    method: 'POST',
                    body: formData
                });
            }
            fetchClassifications();
            handleCloseModal();
            if (onDataChange) onDataChange();
        } catch (err) {
            setError(err.message || 'Erro ao salvar classificação.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Tem certeza que deseja excluir a classificação "${name}"?`)) return;

        try {
            await apiFetch(`/product-classifications/${id}`, { method: 'DELETE' });
            fetchClassifications();
            if (onDataChange) onDataChange();
        } catch (err) {
            alert(err.message || 'Erro ao excluir. Verifique se existem produtos vinculados.');
        }
    };

    const filteredItems = classifications.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-4 md:p-6 max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <List className="text-blue-600" /> Classificação de Produtos
                    </h1>
                    <p className="text-sm text-gray-500">Gerencie as categorias para organização do seu estoque.</p>
                </div>
                {canCreate && (
                    <button 
                        onClick={() => handleOpenModal()}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-semibold flex items-center gap-2 shadow-sm transition-all"
                    >
                        <Plus size={20} /> Nova Classificação
                    </button>
                )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b bg-gray-50 flex items-center gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                        <input 
                            type="text"
                            placeholder="Pesquisar classificação..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-600 font-bold uppercase text-xs">
                            <tr>
                                <th className="px-6 py-4">Nome da Classificação</th>
                                <th className="px-6 py-4 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="2" className="px-6 py-8 text-center text-gray-500">
                                        <Loader2 className="animate-spin inline mr-2" /> Carregando...
                                    </td>
                                </tr>
                            ) : filteredItems.length === 0 ? (
                                <tr>
                                    <td colSpan="2" className="px-6 py-8 text-center text-gray-500 italic">
                                        Nenhuma classificação encontrada.
                                    </td>
                                </tr>
                            ) : (
                                filteredItems.map(item => (
                                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 font-medium text-gray-800">{item.name}</td>
                                        <td className="px-6 py-4 text-right flex justify-end gap-2">
                                            {canEdit && (
                                                <button 
                                                    onClick={() => handleOpenModal(item)}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    title="Editar"
                                                >
                                                    <Edit size={18} />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button 
                                                    onClick={() => handleDelete(item.id, item.name)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Excluir"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                            <h3 className="font-bold text-gray-800 text-lg">
                                {editingItem ? 'Editar Classificação' : 'Nova Classificação'}
                            </h3>
                            <button onClick={handleCloseModal} className="p-1 hover:bg-gray-200 rounded-full transition-colors">
                                <X size={24} />
                            </button>
                        </div>
                        
                        <form onSubmit={handleSubmit}>
                            <div className="p-6 space-y-4">
                                {error && (
                                    <div className="p-3 bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg flex items-center gap-2">
                                        <AlertCircle size={18} /> {error}
                                    </div>
                                ) }
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Nome da Categoria</label>
                                    <input 
                                        type="text"
                                        required
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="Ex: Bebidas, Uso Consumo, Limpeza..."
                                        value={formData.name}
                                        onChange={(e) => setFormData({ name: e.target.value })}
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                                <button 
                                    type="button" 
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 text-gray-700 font-semibold hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit"
                                    disabled={submitting}
                                    className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 shadow-md transition-all flex items-center gap-2 disabled:bg-blue-400"
                                >
                                    {submitting ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                                    Salvar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductClassificationsPage;